import { useState, useMemo } from 'react'
import { useCachedQuery, useCachedMutations } from '@/hooks/useCachedQuery'
import { showToast } from '@/components/layout/Toast'
import Modal from '@/components/layout/Modal'
import type { ExpedienteSunat, MovimientoCaja, EstadoExpediente, TipoExpediente } from '@/lib/types'
import { formatCurrency, formatDate } from '@/lib/utils'
import { descargarCsv, type CsvColumn } from '@/lib/csv'
import { KpiRow, KpiStrip, PageHeader, Pagination, StatCard } from '@/components/ui'
import { usePagination } from '@/hooks/usePagination'
import { IconAlertTriangle, IconDownload, IconEdit, IconFileText, IconImage, IconPaperclip, IconPlus, IconSearch, IconTrash } from '@/components/icons/Icons'

const TIPO_LABEL: Record<string, string> = {
  esquela: 'Esquela',
  carta: 'Carta / Descargo',
  orden_pago: 'Orden de Pago',
  resolucion: 'Resolución',
  requerimiento: 'Requerimiento',
  otro: 'Otro',
}

const ESTADO_LABEL: Record<string, string> = {
  borrador: 'Borrador',
  presentado: 'Presentado',
  en_evaluacion: 'En evaluación',
  aceptado: 'Aceptado',
  rechazado: 'Rechazado',
  desistido: 'Desistido',
}

const ESTADO_BADGE: Record<string, string> = {
  borrador: 'badge-borrador',
  presentado: 'badge-enviada',
  en_evaluacion: 'badge-warning',
  aceptado: 'badge-aceptada',
  rechazado: 'badge-rechazada',
  desistido: 'badge-borrador',
}

const CRUMBS = [{ label: 'Contabilidad', to: '/contabilidad' }, { label: 'Expedientes SUNAT' }]

/** Estados en los que el trámite sigue abierto y por tanto el plazo corre. */
const ABIERTOS = new Set(['borrador', 'presentado', 'en_evaluacion'])

const vacio: Omit<ExpedienteSunat, 'id' | 'created_at' | 'movimientos'> & { movimiento_ids: number[] } = {
  tipo: 'esquela', numero: '', periodo: '', asunto: '', estado: 'borrador',
  fecha_documento: null, fecha_presentacion: null, fecha_respuesta: null, fecha_limite: null,
  monto_disputado: null, moneda: 'PEN', resultado: null, sustento: null,
  adjuntos: [], notas: null, movimiento_ids: [],
}

export default function ExpedientesSunat() {
  const { data: items = [], isLoading: loading } = useCachedQuery<ExpedienteSunat>('expedientes_sunat')
  const { addMut, updateMut, removeMut } = useCachedMutations('expedientes_sunat')
  const { data: movimientos = [] } = useCachedQuery<MovimientoCaja>('movimientos_caja')

  const [modal, setModal] = useState<'create' | 'edit' | null>(null)
  const [form, setForm] = useState(vacio)
  const [editId, setEditId] = useState<number | null>(null)
  const [del, setDel] = useState<ExpedienteSunat | null>(null)
  const [search, setSearch] = useState('')
  const [filtroEstado, setFiltroEstado] = useState('todos')
  const [filtroTipo, setFiltroTipo] = useState('todos')
  const [filtroPeriodo, setFiltroPeriodo] = useState('todos')

  const hoy = new Date().toISOString().slice(0, 10)

  /* ─── Filtrado ─── */
  const filtered = useMemo(() => {
    let result = items
    if (filtroEstado === 'abiertos') result = result.filter(e => ABIERTOS.has(e.estado))
    else if (filtroEstado !== 'todos') result = result.filter(e => e.estado === filtroEstado)
    if (filtroTipo !== 'todos') result = result.filter(e => e.tipo === filtroTipo)
    if (filtroPeriodo !== 'todos') result = result.filter(e => (e.periodo || '').startsWith(filtroPeriodo))
    if (search) {
      const q = search.toLowerCase()
      result = result.filter(e =>
        e.asunto?.toLowerCase().includes(q) ||
        e.numero?.toLowerCase().includes(q) ||
        e.periodo?.toLowerCase().includes(q) ||
        e.notas?.toLowerCase().includes(q)
      )
    }
    // Lo abierto primero, y dentro de eso lo que vence antes.
    return [...result].sort((a, b) => {
      const abierto = (e: ExpedienteSunat) => (ABIERTOS.has(e.estado) ? 0 : 1)
      return abierto(a) - abierto(b)
        || (a.fecha_limite || '9999').localeCompare(b.fecha_limite || '9999')
        || (b.periodo || '').localeCompare(a.periodo || '')
    })
  }, [items, filtroEstado, filtroTipo, filtroPeriodo, search])

  const { page, setPage, totalPages, paginated, pageSize, total } =
    usePagination(filtered, 15, `${search}|${filtroEstado}|${filtroTipo}|${filtroPeriodo}`)

  /* Anos con expedientes: el periodo viene como 'AAAA-MM'. */
  const anios = useMemo(
    () => [...new Set(items.map(e => (e.periodo || '').slice(0, 4)).filter(Boolean))].sort((a, b) => b.localeCompare(a)),
    [items],
  )

  const hayFiltros = !!search || filtroEstado !== 'abiertos' || filtroTipo !== 'todos' || filtroPeriodo !== 'todos'
  const limpiarFiltros = () => { setSearch(''); setFiltroEstado('abiertos'); setFiltroTipo('todos'); setFiltroPeriodo('todos') }

  /* ─── Resumen ───────────────────────────────────────────────────────────
     Lo que importa de un tramite no es cuantos hay, sino cuantos siguen sin
     respuesta y cuantos ya se pasaron del plazo sin que nadie lo mire. */
  const resumen = useMemo(() => {
    const abiertos = items.filter(e => ABIERTOS.has(e.estado))
    return {
      abiertos: abiertos.length,
      vencidos: abiertos.filter(e => e.fecha_limite && e.fecha_limite < hoy).length,
      enDisputa: abiertos.reduce((acc, e) => acc + Number(e.monto_disputado || 0), 0),
      aceptados: items.filter(e => e.estado === 'aceptado').length,
    }
  }, [items, hoy])

  /* ─── CRUD ─── */
  const openCreate = () => { setForm(vacio); setEditId(null); setModal('create') }

  const openEdit = (e: ExpedienteSunat) => {
    setForm({
      tipo: e.tipo, numero: e.numero ?? '', periodo: e.periodo ?? '', asunto: e.asunto,
      estado: e.estado, fecha_documento: e.fecha_documento, fecha_presentacion: e.fecha_presentacion,
      fecha_respuesta: e.fecha_respuesta, fecha_limite: e.fecha_limite,
      monto_disputado: e.monto_disputado, moneda: e.moneda, resultado: e.resultado,
      sustento: e.sustento, adjuntos: e.adjuntos ?? [], notas: e.notas,
      movimiento_ids: (e.movimientos ?? []).map(m => m.id),
    })
    setEditId(e.id)
    setModal('edit')
  }

  const handleSave = async () => {
    if (!form.asunto.trim()) { showToast('Asunto requerido', 'error'); return }
    // La API exige 'YYYY-MM'; un periodo a medias se rechaza con un 400 opaco.
    if (form.periodo && !/^\d{4}-\d{2}$/.test(form.periodo)) {
      showToast('El período debe tener formato AAAA-MM (ej. 2025-06)', 'error'); return
    }
    try {
      const payload = { ...form, periodo: form.periodo || null, numero: form.numero || null }
      if (modal === 'edit' && editId) {
        await updateMut.mutateAsync({ id: editId, ...payload } as never)
        showToast('Expediente actualizado', 'success')
      } else {
        await addMut.mutateAsync(payload as never)
        showToast('Expediente creado', 'success')
      }
      setModal(null)
    } catch {
      showToast('Error al guardar', 'error')
    }
  }

  const handleDelete = async () => {
    if (!del) return
    try {
      await removeMut.mutateAsync(del.id)
      showToast('Expediente eliminado', 'success')
      setDel(null)
    } catch { showToast('Error al eliminar', 'error') }
  }

  const toggleMovimiento = (id: number) => {
    setForm(f => ({
      ...f,
      movimiento_ids: f.movimiento_ids.includes(id)
        ? f.movimiento_ids.filter(x => x !== id)
        : [...f.movimiento_ids, id],
    }))
  }

  const exportarCsv = () => {
    if (filtered.length === 0) { showToast('No hay expedientes que exportar', 'error'); return }
    const cols: CsvColumn<ExpedienteSunat>[] = [
      ['Tipo', e => TIPO_LABEL[e.tipo] || e.tipo],
      ['Numero', e => e.numero || ''],
      ['Periodo', e => e.periodo || ''],
      ['Asunto', e => e.asunto],
      ['Estado', e => ESTADO_LABEL[e.estado] || e.estado],
      ['Fecha Documento', e => e.fecha_documento || ''],
      ['Fecha Presentacion', e => e.fecha_presentacion || ''],
      ['Fecha Limite', e => e.fecha_limite || ''],
      ['Fecha Respuesta', e => e.fecha_respuesta || ''],
      ['Monto Disputado', e => (e.monto_disputado ?? '')],
      ['Moneda', e => e.moneda || 'PEN'],
      ['Resultado', e => e.resultado || ''],
      ['Movimientos Amparados', e => (e.movimientos ?? []).map(m => m.concepto || m.descripcion).join(' | ')],
      ['Sustento', e => (e.sustento ? JSON.stringify(e.sustento) : '')],
      ['Adjuntos', e => (e.adjuntos ?? []).map(a => a.url).join(' | ')],
      ['Notas', e => e.notas || ''],
    ]
    descargarCsv(`expedientes-sunat-${filtroEstado}`, cols, filtered)
    showToast(`${filtered.length} expediente${filtered.length !== 1 ? 's' : ''} exportado${filtered.length !== 1 ? 's' : ''}`, 'success')
  }

  if (loading) return (
    <div className="page-enter">
      <PageHeader breadcrumbs={CRUMBS} />
      <div style={{ color: 'var(--text-secondary)', marginTop: '2rem', textAlign: 'center' }}>
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...
      </div>
    </div>
  )

  return (
    <div className="page-enter page-shell" id="expedientes-sunat">
      <PageHeader
        breadcrumbs={CRUMBS}
        meta={total === items.length
          ? `${items.length} expediente${items.length !== 1 ? 's' : ''}`
          : `${total} de ${items.length}`}
        actions={
          <>
            <button className="btn btn-secondary" onClick={exportarCsv} title="Descarga en CSV lo que esta filtrado">
              <IconDownload size={15} /> Exportar
            </button>
            <button className="btn btn-primary" onClick={openCreate} id="btn-new-expediente">
              <IconPlus size={16} /> Nuevo Expediente
            </button>
          </>
        }
      />

      {items.length > 0 && (
        <KpiStrip>
          <KpiRow>
            <StatCard label="Abiertos" value={resumen.abiertos} tone={resumen.abiertos > 0 ? 'warning' : 'default'} />
            <StatCard label="Plazo vencido" value={resumen.vencidos} tone={resumen.vencidos > 0 ? 'danger' : 'default'} />
            <StatCard label="En disputa" value={formatCurrency(resumen.enDisputa, 'PEN')} tone="info" />
            <StatCard label="Aceptados" value={resumen.aceptados} tone="success" />
          </KpiRow>
        </KpiStrip>
      )}

      <div className="filters-bar">
        <div className="filters-bar__search">
          <IconSearch size={14} className="filters-bar__search-icon" />
          <input className="form-input" placeholder="Buscar por asunto, número o período..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-input" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} style={{ width: 180 }}>
          <option value="abiertos">Solo abiertos</option>
          <option value="todos">Todos los estados</option>
          {Object.entries(ESTADO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="form-input" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)} style={{ width: 165 }}>
          <option value="todos">Todos los tipos</option>
          {Object.entries(TIPO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {anios.length > 1 && (
          <select className="form-input" value={filtroPeriodo} onChange={e => setFiltroPeriodo(e.target.value)} style={{ width: 105 }}>
            <option value="todos">Período</option>
            {anios.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        )}
        {hayFiltros && (
          <button className="btn btn-sm btn-secondary filters-bar__clear" onClick={limpiarFiltros}>Limpiar filtros</button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="empty-state">
          <h3>Sin expedientes</h3>
          <p>Registra una esquela, un descargo o una resolución de SUNAT para seguirle el rastro.</p>
          <button className="btn btn-primary" onClick={openCreate}>Nuevo Expediente</button>
        </div>
      ) : (
        <>
        <div className="table-container page-shell__table">
          <table>
            <thead>
              <tr>
                <th>Período</th>
                <th>Tipo</th>
                <th>Asunto / N°</th>
                <th>Estado</th>
                <th>Presentado</th>
                <th>Plazo</th>
                <th>Ampara</th>
                <th>Adj.</th>
                <th style={{ width: 90 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    Sin resultados para el filtro aplicado
                  </td>
                </tr>
              ) : paginated.map(e => {

                const vencido = ABIERTOS.has(e.estado) && e.fecha_limite && e.fecha_limite < hoy
                return (
                  <tr key={e.id} className={!ABIERTOS.has(e.estado) ? 'row--dimmed' : undefined}>
                    <td className="expedientes__periodo">{e.periodo || '—'}</td>
                    <td><span className="badge badge-categoria">{TIPO_LABEL[e.tipo] || e.tipo}</span></td>
                    <td>
                      <div className="cell-stack">
                        <span className="cell-stack__title">{e.asunto}</span>
                        {e.numero && <span className="cell-stack__sub">N° {e.numero}</span>}
                      </div>
                    </td>
                    <td><span className={`badge ${ESTADO_BADGE[e.estado] || ''}`}>{ESTADO_LABEL[e.estado] || e.estado}</span></td>
                    <td className="expedientes__fecha">{e.fecha_presentacion ? formatDate(e.fecha_presentacion) : '—'}</td>
                    <td className="expedientes__fecha">
                      {e.fecha_limite
                        ? <span className={vencido ? 'expedientes__vencido' : undefined}>
                            {formatDate(e.fecha_limite)}
                            {vencido && <IconAlertTriangle size={12} style={{ marginLeft: 4, verticalAlign: '-1px' }} />}
                          </span>
                        : '—'}
                    </td>
                    <td>
                      {/* Un expediente ampara N movimientos (una carta cubre la
                          multa de IGV y la de Renta del mismo periodo). */}
                      {(e.movimientos?.length ?? 0) > 0
                        ? <span className="badge badge-adjunto" title={e.movimientos!.map(m => m.concepto || m.descripcion).join('\n')}>
                            {e.movimientos!.length} mov · {formatCurrency(
                              e.movimientos!.reduce((a, m) => a + Number(m.monto || 0), 0),
                              e.movimientos![0]?.moneda || 'PEN',
                            )}
                          </span>
                        : <span className="caja-sin-comprobante">—</span>}
                    </td>
                    <td>
                      {(e.adjuntos?.length ?? 0) > 0
                        ? <div className="row-actions row-actions--nowrap">
                            {e.adjuntos!.map((a, i) => (
                              <a key={i} href={a.url} target="_blank" rel="noreferrer" title={a.nombre} className="badge badge-adjunto">
                                {a.tipo?.includes('pdf')
                                  ? <><IconFileText size={11} /> PDF</>
                                  : a.tipo?.startsWith('image/')
                                    ? <><IconImage size={11} /> IMG</>
                                    : <IconPaperclip size={11} />}
                              </a>
                            ))}
                          </div>
                        : <span className="caja-sin-comprobante">—</span>}
                    </td>
                    <td>
                      <div className="row-actions row-actions--nowrap">
                        <button className="btn btn-sm btn-secondary" onClick={() => openEdit(e)} title="Editar"><IconEdit size={14} /></button>
                        <button className="btn btn-sm btn-danger" onClick={() => setDel(e)} title="Eliminar"><IconTrash size={14} /></button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize}
          onPage={setPage} noun="expedientes" />
        </>
      )}

      {/* ─── Modal crear / editar ─── */}
      <Modal isOpen={modal !== null} onClose={() => setModal(null)}
        title={modal === 'edit' ? 'Editar Expediente' : 'Nuevo Expediente'}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
            <button className="btn btn-primary" onClick={handleSave}>Guardar</button>
          </>
        }>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Tipo</label>
            <select className="form-input" value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value as TipoExpediente }))}>
              {Object.entries(TIPO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Estado</label>
            <select className="form-input" value={form.estado} onChange={e => setForm(f => ({ ...f, estado: e.target.value as EstadoExpediente }))}>
              {Object.entries(ESTADO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Asunto *</label>
          <input className="form-input" value={form.asunto} onChange={e => setForm(f => ({ ...f, asunto: e.target.value }))}
            placeholder="Ej: Solicitud de ajuste de multa — Esquela de Infracción" />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">N° de documento</label>
            <input className="form-input" value={form.numero ?? ''} onChange={e => setForm(f => ({ ...f, numero: e.target.value }))}
              placeholder="Ej: 009798220257E1300" />
          </div>
          <div className="form-group">
            <label className="form-label">Período (AAAA-MM)</label>
            <input className="form-input" value={form.periodo ?? ''} onChange={e => setForm(f => ({ ...f, periodo: e.target.value }))}
              placeholder="2025-06" />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Fecha del documento</label>
            <input type="date" className="form-input" value={form.fecha_documento ?? ''}
              onChange={e => setForm(f => ({ ...f, fecha_documento: e.target.value || null }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Fecha de presentación</label>
            <input type="date" className="form-input" value={form.fecha_presentacion ?? ''}
              onChange={e => setForm(f => ({ ...f, fecha_presentacion: e.target.value || null }))} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Plazo de respuesta</label>
            <input type="date" className="form-input" value={form.fecha_limite ?? ''}
              onChange={e => setForm(f => ({ ...f, fecha_limite: e.target.value || null }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Fecha de respuesta</label>
            <input type="date" className="form-input" value={form.fecha_respuesta ?? ''}
              onChange={e => setForm(f => ({ ...f, fecha_respuesta: e.target.value || null }))} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Monto en disputa</label>
            <input type="number" step="0.01" className="form-input" value={form.monto_disputado ?? ''}
              onChange={e => setForm(f => ({ ...f, monto_disputado: e.target.value === '' ? null : Number(e.target.value) }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Moneda</label>
            <select className="form-input" value={form.moneda} onChange={e => setForm(f => ({ ...f, moneda: e.target.value }))}>
              <option value="PEN">PEN</option>
              <option value="USD">USD</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Movimientos que ampara</label>
          <p className="form-hint">
            Los egresos de caja cubiertos por este trámite. Una carta suele amparar dos
            multas del mismo período (IGV y Renta).
          </p>
          <div className="expedientes__movsel">
            {movimientos.length === 0 && <span className="caja-sin-comprobante">No hay movimientos cargados.</span>}
            {movimientos.slice(0, 60).map(m => (
              <label key={m.id} className="expedientes__movopt">
                <input type="checkbox" checked={form.movimiento_ids.includes(m.id)} onChange={() => toggleMovimiento(m.id)} />
                <span>{formatDate(m.fecha)} · {m.concepto || m.descripcion} · {formatCurrency(m.monto, m.moneda)}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Resultado</label>
          <input className="form-input" value={form.resultado ?? ''} onChange={e => setForm(f => ({ ...f, resultado: e.target.value || null }))}
            placeholder="Se completa cuando SUNAT responde" />
        </div>

        <div className="form-group">
          <label className="form-label">Notas</label>
          <textarea className="form-input" rows={3} value={form.notas ?? ''}
            onChange={e => setForm(f => ({ ...f, notas: e.target.value || null }))}
            placeholder="Números de orden citados, gestiones, contacto en SUNAT..." />
        </div>
      </Modal>

      {/* ─── Confirmación de borrado ─── */}
      <Modal isOpen={del !== null} onClose={() => setDel(null)} title="Eliminar expediente"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setDel(null)}>Cancelar</button>
            <button className="btn btn-danger" onClick={handleDelete}>Eliminar</button>
          </>
        }>
        <p>¿Eliminar <strong>{del?.asunto}</strong>? Se borran también sus enlaces a movimientos de caja. Los movimientos en sí no se tocan.</p>
      </Modal>
    </div>
  )
}
